/**
 * Open/close state of the A3C everywhere chat (V2306041186).
 *
 * ChromeUI owns panel visibility (`shared.embeddedChat.isOpen`). The slice
 * starts from the legacy `openByDefault ?? true`; EmbeddedChatManager then
 * runs {@link resolveInitialChatOpenState} once its inputs are known — the
 * everywhere weblab (`useA3cEverywhereEnabled`), the effective agent and the
 * page's a3cConfig — and dispatches the result before the panel is ever shown.
 * Rules, in priority order (see the requirements on V2306041186):
 *
 *   1. Page-owner config wins — an explicit `openByDefault` is honored as-is.
 *   2. User closed the chat in this tab → stays closed until reopened.
 *   3. Active conversation → stays open across page transitions.
 *   4. Otherwise the destination default: embedded (push) open, flyover closed.
 *
 * Only the everywhere default agent is in scope. Pages that wire their own
 * agent, and pages where the everywhere weblab is off, keep the legacy
 * `openByDefault ?? true` behavior byte-for-byte.
 *
 * This module is a leaf: it reads storage and nothing else, so the same
 * resolver runs for the first decision and for every re-resolution (late
 * a3cConfig, late weblab) without the two ever disagreeing.
 */

// ── Storage contract ─────────────────────────────────────────────────────────

/**
 * sessionStorage flag (value `'true'`) set when the user closes the chat and
 * cleared on any open. Per tab by construction; deliberately NOT customer
 * scoped — it is a UI preference for the tab, not conversation state.
 * Owned by ChromeUI (this module).
 */
export const A3C_CHAT_USER_CLOSED_KEY = 'a3c_chat_user_closed';

/**
 * Cross-package contract with A3ChatAssets (`src/utils/threadSession.ts` and
 * `src/providers/ExternalMessageHandler.tsx`). A3ChatAssets WRITES these;
 * ChromeUI only READS them here. Any change on either side must be made in
 * both packages.
 *
 * - `a3c_thread_session_<customerId>` (localStorage): JSON
 *   `{ threadId, welcomeOnly, lastActiveAt? }` — the last-viewed thread and
 *   whether the user has engaged it (`welcomeOnly === false`).
 * - `a3c_tab_active` (sessionStorage, `'true'`): this tab has already
 *   resolved a thread, i.e. it is not a brand-new tab.
 * - 8h: the resume-staleness window A3ChatAssets applies to `lastActiveAt`.
 */
export const A3C_THREAD_SESSION_KEY_PREFIX = 'a3c_thread_session';
export const A3C_TAB_ACTIVE_KEY = 'a3c_tab_active';
export const A3C_CONVERSATION_STALE_MS = 8 * 60 * 60 * 1000;

// ── Storage access (all best-effort; storage may be unavailable) ─────────────

const getSessionItem = (key: string): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const getLocalItem = (key: string): string | null => {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

/** True when the user closed the chat earlier in this tab session. */
export const isChatUserClosed = (): boolean =>
  getSessionItem(A3C_CHAT_USER_CLOSED_KEY) === 'true';

/**
 * Record (or clear) the user-closed flag. Called by EmbeddedChatManager on
 * every open/close transition, whatever its source — the panel X, the sidenav
 * or focus-header toggles, or an SDK/page-driven open. Clearing on ANY open is
 * what lets a page-driven open (context clip, sent message) stick across the
 * next navigation instead of snapping shut again.
 */
export const setChatUserClosed = (closed: boolean): void => {
  try {
    if (closed) {
      globalThis.sessionStorage?.setItem(A3C_CHAT_USER_CLOSED_KEY, 'true');
    } else {
      globalThis.sessionStorage?.removeItem(A3C_CHAT_USER_CLOSED_KEY);
    }
  } catch {
    // Best-effort — sessionStorage may be unavailable or full.
  }
};

interface ThreadSessionState {
  threadId: string;
  welcomeOnly: boolean;
  lastActiveAt?: number;
}

/**
 * Same key derivation as A3ChatAssets `threadSession.getStorageKey()`:
 * `getCustomerId()` there is `window.takt_user_id ?? ''`, and an empty
 * customer id drops the suffix entirely.
 */
const getThreadSessionKey = (): string => {
  const customerId = (window as unknown as { takt_user_id?: string })
    .takt_user_id;
  return customerId
    ? `${A3C_THREAD_SESSION_KEY_PREFIX}_${customerId}`
    : A3C_THREAD_SESSION_KEY_PREFIX;
};

const readThreadSession = (): ThreadSessionState | null => {
  const raw = getLocalItem(getThreadSessionKey());
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.threadId === 'string' &&
      typeof parsed.welcomeOnly === 'boolean'
    ) {
      return parsed as ThreadSessionState;
    }
  } catch {
    // Malformed — treat as absent.
  }
  return null;
};

/**
 * "Active conversation" = an ENGAGED thread that A3ChatAssets will RESUME on
 * this page load. Mirrors the resume branch of ExternalMessageHandler's
 * thread-resolution effect so the two decisions cannot disagree:
 *
 * - engaged: `welcomeOnly === false` (user sent a message or selected a
 *   thread). A greeting-only thread is not active, so a persisted welcome
 *   message never forces the chat open on a flyover page.
 * - same tab: `a3c_tab_active` is set. A new tab starts a fresh thread, so it
 *   also starts from the default open/close state.
 * - not stale: `lastActiveAt` within 8h. A stale thread starts fresh.
 *
 * Nothing here depends on the welcome-message weblab: when it is off,
 * `welcomeOnly` is never written `true`, so "active" degrades to "the user
 * engaged in this tab", which is the accepted behavior.
 */
export const hasActiveConversation = (now: number = Date.now()): boolean => {
  const session = readThreadSession();
  if (!session || session.welcomeOnly) return false;
  if (getSessionItem(A3C_TAB_ACTIVE_KEY) !== 'true') return false;
  if (
    typeof session.lastActiveAt === 'number' &&
    session.lastActiveAt > 0 &&
    now - session.lastActiveAt > A3C_CONVERSATION_STALE_MS
  ) {
    return false;
  }
  return true;
};

// ── Resolver ─────────────────────────────────────────────────────────────────

/**
 * Why the state came out the way it did. Logged by EmbeddedChatManager so the
 * transition matrix can be verified in production.
 */
export type ChatInitialOpenReason =
  | 'out_of_scope'
  | 'page_config'
  | 'user_closed'
  | 'active_conversation'
  | 'default_embedded'
  | 'default_flyover';

export interface ChatInitialOpenState {
  isOpen: boolean;
  reason: ChatInitialOpenReason;
}

/**
 * Inputs to {@link resolveInitialChatOpenState}. Plain values so the resolver
 * is a pure function of its arguments plus storage — EmbeddedChatManager
 * sources them from the page config, URL rules and the weblab hooks it
 * already evaluates.
 */
export interface ResolveInitialChatOpenStateInputs {
  /** The page's explicit `openByDefault`, if any (embedded config first, then flyover). */
  pageOpenByDefault: boolean | undefined;
  /** True when the panel overlays the page (no page embedded config, not a forced-embedded URL). */
  isFlyover: boolean;
  /** True when the everywhere default agent is the one that will load. */
  isEverywhereDefaultAgent: boolean;
  /** True when the applicable everywhere weblab is on and not vetoed (`useA3cEverywhereEnabled`). */
  isEverywhereEnabled: boolean;
}

export const resolveInitialChatOpenState = ({
  pageOpenByDefault,
  isFlyover,
  isEverywhereDefaultAgent,
  isEverywhereEnabled,
}: ResolveInitialChatOpenStateInputs): ChatInitialOpenState => {
  // Out of scope → legacy behavior, unchanged.
  if (!isEverywhereEnabled || !isEverywhereDefaultAgent) {
    return { isOpen: pageOpenByDefault ?? true, reason: 'out_of_scope' };
  }
  // Rule 1: page-owner config wins when explicitly set.
  if (pageOpenByDefault !== undefined) {
    return { isOpen: pageOpenByDefault, reason: 'page_config' };
  }
  // Rule 2: the user closed it in this tab.
  if (isChatUserClosed()) {
    return { isOpen: false, reason: 'user_closed' };
  }
  // Rule 3: an engaged conversation is being resumed.
  if (hasActiveConversation()) {
    return { isOpen: true, reason: 'active_conversation' };
  }
  // Rule 4: destination default.
  return isFlyover
    ? { isOpen: false, reason: 'default_flyover' }
    : { isOpen: true, reason: 'default_embedded' };
};
